import { z } from 'zod';

/**
 * Conservation des candidats (docs/SECURITE_RGPD.md § Conservation) : échéance,
 * préavis, prolongation, anonymisation automatique et suppression fiche par
 * fiche. L'échéance se calcule en base (vue `v_conservation`, migration 010),
 * pour que l'API et le worker appliquent la même règle.
 */

/** Une prolongation repousse l'échéance de deux ans. */
export const PROLONGATION_MOIS = 24;

/** Commandes de la file `commandes`, déposées par l'API ou planifiées. */
export const COMMANDE_PURGE = 'purger-conservation';
export const COMMANDE_EFFACER_FICHIERS = 'effacer-fichiers';

/** La purge tourne chaque nuit à 2 h 30, heure de Tunis. */
export const PLANIFICATION_PURGE = {
  id: 'purge-conservation-nocturne',
  cron: '30 2 * * *',
  fuseau: 'Africa/Tunis',
} as const;

/** POST /personnes/:id/supprimer — super admin, une personne à la fois. */
/**
 * Ce qui entre au journal des purges quand la suppression est confirmée sans
 * motif écrit (demande du 2026-09-19 : « demander juste confirmation »). La
 * trace nominative reste au journal d'audit — qui a supprimé, et quand.
 */
export const MOTIF_SUPPRESSION_SANS_TEXTE = 'Suppression confirmée depuis la fiche, sans motif écrit';

/**
 * Le motif était obligatoire (5 caractères au moins) : le bouton restait donc
 * inactif, et la suppression paraissait cassée. Il est facultatif depuis le
 * 2026-09-19 ; écrit, il entre au journal des purges, sans nom ni coordonnées.
 */
export const suppressionPersonneSchema = z
  .object({
    motif: z.string().trim().max(500).optional(),
  })
  .strict();
export type SuppressionPersonne = z.infer<typeof suppressionPersonneSchema>;

export const TYPES_PURGE = ['anonymisation', 'suppression'] as const;
export type TypePurge = (typeof TYPES_PURGE)[number];

export const LIBELLES_TYPE_PURGE: Record<TypePurge, string> = {
  anonymisation: 'Anonymisation',
  suppression: 'Suppression définitive',
};

/** Une personne dont l'anonymisation est annoncée (GET /conservation). */
export interface FicheEnPreavis {
  personneId: string;
  nom: string | null;
  prenom: string | null;
  /** Candidature la plus récente, pour ouvrir la fiche. */
  candidatureId: string | null;
  annonceIntitule: string | null;
  nbCandidatures: number;
  /** Candidature reçue ou contact effectif le plus récent (ISO). */
  derniereActivite: string;
  /** AAAA-MM-JJ */
  echeance: string;
  /** AAAA-MM-JJ — l'anonymisation part la nuit qui suit cette date. */
  purgePrevueLe: string;
  prolongeeLe: string | null;
}

export interface EntreePurge {
  id: string;
  type: TypePurge;
  nbFiches: number;
  nbFichiers: number;
  /** Vide pour la purge automatique. */
  operateur: string | null;
  motif: string | null;
  executeeLe: string;
}

export interface EtatConservation {
  mois: number;
  preavisJours: number;
  enPreavis: FicheEnPreavis[];
  purges: EntreePurge[];
  /** Fichiers de fiches purgées que le worker n'a pas encore pu effacer. */
  fichiersEnAttente: number;
}

/** GET /conservation/resume — pour le bandeau de la page d'accueil. */
export interface ResumeConservation {
  enPreavis: number;
  prochainePurge: string | null;
}

/** POST /personnes/:id/prolonger */
export interface ConservationFiche {
  echeance: string;
  purgePrevueLe: string | null;
  prolongeeLe: string | null;
}

/** POST /personnes/:id/supprimer */
export interface BilanSuppression {
  candidatures: number;
  fichiers: number;
}

const JOUR = /^(\d{4})-(\d{2})-(\d{2})$/;

function instantDuJour(jour: string): number {
  const lu = JOUR.exec(jour);
  if (!lu) throw new Error(`Date attendue au format AAAA-MM-JJ : ${jour}`);
  return Date.UTC(Number(lu[1]), Number(lu[2]) - 1, Number(lu[3]));
}

/** Jours calendaires de `depuis` à `jusqua` (AAAA-MM-JJ), négatif si `jusqua` est passé. */
export function joursEntre(depuis: string, jusqua: string): number {
  return Math.round((instantDuJour(jusqua) - instantDuJour(depuis)) / 86_400_000);
}

/**
 * La purge tourne à 2 h 30 : une fiche prévue au plus tard demain part la nuit
 * prochaine, les autres « dans N jours ».
 */
export function quandPurge(purgePrevueLe: string, aujourdhui: string): string {
  const jours = joursEntre(aujourdhui, purgePrevueLe);
  return jours <= 1 ? 'la nuit prochaine' : `dans ${jours} jours`;
}
